import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionSummary, formatDate } from '../utils/subscription';
import {
 ClockIcon,
 ShieldCheckIcon,
 ExclamationTriangleIcon,
 StarIcon
} from '@heroicons/react/24/outline';

const SubscriptionStatus = ({ className ="" }) => {
 const navigate = useNavigate();
 const [subscriptionSummary, setSubscriptionSummary] = useState(null);
 const [isLoading, setIsLoading] = useState(true);
 const [currentTime, setCurrentTime] = useState(new Date());

  // Tenant students always have full access — skip subscription UI entirely
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  
  const hostname = window.location.hostname;
  const isMainPlatform = hostname === 'theskillgenie.com' || 
                         hostname === 'www.theskillgenie.com' || 
                         hostname === 'localhost' || 
                         hostname === '127.0.0.1';

  // Tenant student if on a custom domain/subdomain OR if user has tenant/college info
  const isTenantStudent = !isMainPlatform || 
                          (storedUser?.role === 'student' && (storedUser?.tenant || storedUser?.college_id || storedUser?.college || storedUser?.tenant_id));

 useEffect(() => {
 if (isTenantStudent) {
   setIsLoading(false);
   return;
 }
 const loadSubscriptionSummary = async () => {
 const userId = localStorage.getItem('user_id');
 const summary = await getSubscriptionSummary(userId);
 setSubscriptionSummary(summary);
 setIsLoading(false);
 };

 loadSubscriptionSummary();
 }, [isTenantStudent]);

 // Update time every 30 seconds for real-time countdown
 useEffect(() => {
 const interval = setInterval(() => {
 setCurrentTime(new Date());
 // Reload subscription summary every 30 seconds to update countdown
 const userId = localStorage.getItem('user_id');
 if (userId) {
 getSubscriptionSummary(userId).then(setSubscriptionSummary);
 }
 }, 30000); // Update every 30 seconds

 return () => clearInterval(interval);
 }, []);

  // Tenant students have unconditional full access — render nothing
  if (isTenantStudent) return null;

  if (isLoading || !subscriptionSummary) {
    return (
      <div className={`bg-bgSection text-muted p-3 rounded-lg border border-accentGray/20 ${className}`}>
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          <span className="text-sm">Loading subscription...</span>
        </div>
      </div>
    );
  }

 if (subscriptionSummary.accessType === 'subscription' && subscriptionSummary.isValid) {
  return (
    <div 
      className={`bg-gradient-to-r from-primary to-secondary text-white p-3 rounded-lg cursor-pointer hover:shadow-lg transition-all ${className}`}
      onClick={() => navigate('/mockinterviewpayments_history')}
      title="Click to view payment history"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="h-5 w-5" />
          <span className="font-semibold text-white">Premium Active</span>
        </div>
        <div className="text-sm font-bold">
          {subscriptionSummary.daysLeft > 1 
            ? `${subscriptionSummary.daysLeft} days left`
            : subscriptionSummary.daysLeft === 1
            ? '1 day left'
            : 'Expires today'
          }
        </div>
      </div>
    </div>
  );
 }

 if (subscriptionSummary.accessType === 'trial') {
  return (
    <div className={`bg-gradient-to-r from-primary/80 to-secondary/80 text-white p-3 rounded-lg border border-primary/20 shadow-sm ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClockIcon className="h-5 w-5" />
          <span className="font-semibold text-white">Free Trial</span>
        </div>
        <div className="text-sm font-bold">
          {subscriptionSummary.daysLeft > 1 
            ? `${subscriptionSummary.daysLeft} days left`
            : subscriptionSummary.daysLeft === 1
            ? '1 day left'
            : 'Expires today'
          }
        </div>
      </div>
    </div>
  );
 }

 if (subscriptionSummary.accessType === 'expired') {
 return (
 <div className={`bg-gradient-to-r from-red-500 to-red-600 text-white p-3 rounded-lg ${className}`}>
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <ExclamationTriangleIcon className="h-5 w-5" />
 <span className="font-semibold">Access Expired</span>
 </div>
 <div className="text-sm">
 Subscribe to continue
 </div>
 </div>
 </div>
 );
 }

 return null;
};

export default SubscriptionStatus;



